<template>
  <div
    class="flex gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
    :class="{ 'bg-blue-50 hover:bg-blue-50': !notification.is_read }"
    @click="handleClick"
  >
    <!-- Unread dot -->
    <div class="flex-shrink-0 mt-1.5">
      <span
        v-if="!notification.is_read"
        class="block w-2 h-2 rounded-full bg-blue-500"
      />
      <span v-else class="block w-2 h-2" />
    </div>

    <!-- Content -->
    <div class="flex-1 min-w-0">
      <p class="text-sm font-medium text-gray-900 leading-snug">{{ notification.title }}</p>
      <p class="text-sm text-gray-600 mt-0.5 leading-snug">{{ notification.body }}</p>
      <p class="text-xs text-gray-400 mt-1">{{ relativeTime }}</p>

      <!-- Invitation actions -->
      <div
        v-if="showActions && notification.type === 'group_invitation' && invitationPending"
        class="flex gap-2 mt-2"
        @click.stop
      >
        <button
          :disabled="acting"
          @click="handleAccept"
          class="px-3 py-1 text-xs font-medium bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-50 transition-colors"
        >
          Accept
        </button>
        <button
          :disabled="acting"
          @click="handleDecline"
          class="px-3 py-1 text-xs font-medium bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          Decline
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useNotificationStore } from '../../stores/notifications'
import type { Notification } from '../../services/notificationService'

const props = defineProps<{
  notification: Notification
  showActions?: boolean
}>()

const store = useNotificationStore()
const acting = ref(false)

// Derived invitation id from the notification payload
const invitationId = computed(() => {
  const payload = props.notification.payload as Record<string, unknown>
  return typeof payload.invitation_id === 'number' ? payload.invitation_id : null
})

// Only show Accept/Decline if we have a pending invitation for this notification
const invitationPending = computed(() => {
  if (!invitationId.value) return false
  return store.pendingInvitations.some(inv => inv.id === invitationId.value)
})

// Simple relative timestamp (e.g. "5m ago", "2h ago", "3d ago")
const relativeTime = computed(() => {
  const diff = Date.now() - new Date(props.notification.created_at).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
})

async function handleClick() {
  if (!props.notification.is_read) {
    await store.markAsRead(props.notification.id)
  }
}

async function handleAccept() {
  if (!invitationId.value) return
  acting.value = true
  try {
    await store.acceptInvitation(invitationId.value)
  } finally {
    acting.value = false
  }
}

async function handleDecline() {
  if (!invitationId.value) return
  acting.value = true
  try {
    await store.declineInvitation(invitationId.value)
  } finally {
    acting.value = false
  }
}
</script>
